<template>
  <main>
    <InvoiceDetails class="details" v-if="isInvoiceEnabled" />
    <TutorDetails :tutor="tutor" class="details" v-else />
    <section class="reservation-section">
      <h2 class="section-title">{{ $t(`reservation.${reservationTitle}`) }}</h2>
      <p class="section-desc">
        {{ reservationDetails }}
      </p>
      <div class="breadcrumb-container" v-once>
        <Breadcrumb
          :items="breadcrumbLinks"
          icon="chevron-right"
          showActions
          showNote
          note="reservationNote"
          color="#5FD068"
        />
      </div>
      <router-view></router-view>
    </section>
  </main>
</template>

<script>
import GlobalMixin from "@/GlobalMixin";
import TutorDetails from "../../components/NewReservation/TutorDetails.vue";
import InvoiceDetails from "./InvoiceDetails.vue";
import Breadcrumb from "../../components/global/Breadcrumb.vue";

export default {
  name: "ReservationView",
  mixins: [GlobalMixin],
  components: {
    TutorDetails,
    InvoiceDetails,
    Breadcrumb,
  },
  data() {
    return {
      tutorId: this.$route.params.id,
      tutor: {},
      breadcrumbLinks: [
        {
          id: 1,
          text: "chooseCourses",
          componentName: "Courses",
          href: "courses",
        },
        {
          id: 2,
          text: "membershipProgram",
          componentName: "ProgramsList",
          href: "programs",
        },
        {
          id: 3,
          text: "mySchedule",
          componentName: "SessionsList",
          href: "sessions",
        },
        {
          id: 4,
          text: "checkout",
          componentName: "Payment",
          href: "checkout",
        },
      ],
      configs: JSON.parse(localStorage.getItem("configs")),
      isOperator:
        JSON.parse(localStorage.getItem("vuex")).auth?.user?.user?.user_type ===
        "operator",
    };
  },
  /**
   * Initialize Component
   */
  mounted() {
    window.addEventListener("localstorageChange", (event) => {
      this.configs = JSON.parse(event.detail.storage.configs);
    });
    if (this.$store.getters.GET_SELECTED_TUTOR_DETAILS) {
      this.tutor = this.$store.getters.GET_SELECTED_TUTOR_DETAILS;
    } else {
      this.getTutorDetails();
    }
  },
  destroyed() {
    window.removeEventListener("localstorageChange", () => {});
  },
  /**
   * Reset all reservation state when navigating outside of reservation view
   */
  beforeRouteLeave(to, from, next) {
    this.$store.dispatch("resetSelections");
    next();
  },
  methods: {
    /**
     * Get all details of a specific tutor
     */
    getTutorDetails() {
      this.axios
        .post(
          `${process.env.VUE_APP_APIEndPoint}tutor/detail/${this.tutorId}/`,
          {},
          this.headers
        )
        .then((res) => {
          this.tutor = res.data.data.tutor;
          this.$store.dispatch("saveSelectedTutorDetails", res.data.data.tutor);
        });
    },
  },
  computed: {
    /**
     * Check if the current router is Payment page
     */
    isInvoiceEnabled() {
      return this.$route.name === "Payment";
    },
    /**
     * Get Reservation Title for every routing
     */
    reservationTitle() {
      const componentName = this.$route.name;
      switch (componentName) {
        case "Courses":
          return "courses";
        case "ProgramsList":
          return "chooseAProgram";
        case "SessionsList":
          return "schedule";
        case "Payment":
          return "checkout";
        default:
          return "";
      }
    },
    /**
     * Get Description foreach view
     */
    reservationDetails() {
      const componentName = this.$route.name;
      switch (componentName) {
        case "Courses":
          return this.configs?.courses?.config_value;
        case "ProgramsList":
          return this.configs?.choose_program?.config_value;
        case "SessionsList":
          return this.configs?.schedule_course?.config_value;
        case "Payment":
          return this.configs?.checkout?.config_value;
        default:
          return "";
      }
    },
  },
  watch: {
    /**
     * Watch for language change
     */
    "$i18n.locale"() {
      this.headers.headers["accept-language"] = localStorage.lang;
      this.getconfigurations();
      this.getTutorDetails();
    },
  },
};
</script>

<style lang="scss" scoped>
main {
  display: flex;
  gap: 24px;
  max-width: $max-xl-screen;
  padding: 0 40px;
  margin: 0 auto 51px;
  .details {
    min-width: 440px;
    width: 440px;
  }
  .reservation-section {
    width: 100%;
    font-family: $font-family;
    font-style: normal;
    .section-title {
      font-weight: 600;
      font-size: 28px;
      line-height: 34px;
      color: $title;
      margin-bottom: 11px;
    }
    .section-desc {
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      color: $paragraph;
      margin-bottom: 19px;
    }
    .breadcrumb-container {
      padding: 20px;
      background: $white;
      border: 1px solid $paragraph;
      border-radius: 8px;
      font-family: $font-family;
      font-style: normal;
      margin-bottom: 24px;
    }
  }
}
::v-deep .v-breadcrumbs {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0;
  li {
    padding: 0;
    a {
      font-weight: 500;
      font-size: 18px;
      line-height: 25px;
    }
  }
  .v-breadcrumbs__divider {
    .v-icon {
      font-size: 24px;
      color: $black;
    }
  }
}
@media all and (min-width: $min-md-screen) and (max-width: $max-md-screen) {
  main {
    .details {
      min-width: 255px;
      width: 255px;
    }
    .reservation-section {
      .section-title {
        font-weight: 600;
        font-size: 20px;
        line-height: 23.44px;
      }
      .breadcrumb-container {
        padding: 20px;
        margin-bottom: 16px;
      }
    }
  }
  ::v-deep .v-breadcrumbs {
    gap: 2px;
    padding: 0;
    li {
      padding: 0;
      a {
        font-size: 16px;
        line-height: 16px;
      }
    }
    .v-breadcrumbs__divider {
      .v-icon {
        font-size: 20px;
      }
    }
  }
}
@media all and (max-width: $min-md-screen) {
  main {
    max-width: $max-sm-screen;
    min-width: $min-sm-screen;
    flex-direction: column;
    padding: 0 16px;
    .details {
      min-width: 100%;
      width: 100%;
      margin-bottom: 18px;
    }
    .reservation-section {
      .section-title {
        margin-bottom: 8px;
      }
      .section-desc {
        font-size: 12px;
        line-height: 24px;
        margin-bottom: 11px;
      }
      .breadcrumb-container {
        padding: 8px;
      }
    }
  }
  ::v-deep .v-breadcrumbs {
    flex-wrap: nowrap;
    li {
      padding: 0;
      text-align: center;
      a {
        font-size: 12px;
        line-height: 16px;
      }
    }
    .v-breadcrumbs__divider {
      .v-icon {
        font-size: 16px;
      }
    }
  }
}
</style>
